import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { RetirementState, RetirementAsset } from '@/types'

const initialState: RetirementState = {
  currentAge: 30,
  retirementAge: 65,
  lifeExpectancy: 90,
  currentSavings: 0,
  annualContribution: 0,
  contributionAsPercentOfIncome: 0,
  contributionMode: 'fixed',
  expectedReturn: 7,
  inflationRate: 3,
  desiredIncome: 60000,
  socialSecurityIncome: 0,
  socialSecurityStartAge: 67,
  requiredSavings: 0,
  successRate: 0,
  portfolio: [],
  chartData: {
    labels: [],
    portfolioValues: [],
    requiredSavings: [],
  },
}

const retirementSlice = createSlice({
  name: 'retirement',
  initialState,
  reducers: {
    updateRetirementSettings: (state, action: PayloadAction<{ setting: keyof RetirementState; value: unknown }>) => {
      const { setting, value } = action.payload
      // @ts-expect-error - Dynamic assignment
      state[setting] = value
      retirementSlice.caseReducers.calculateRetirement(state)
    },
    addPortfolioAsset: (state, action: PayloadAction<RetirementAsset>) => {
      state.portfolio.push(action.payload)
    },
    removePortfolioAsset: (state, action: PayloadAction<number>) => {
      state.portfolio.splice(action.payload, 1)
    },
    updatePortfolioAsset: (state, action: PayloadAction<{ index: number; updates: Partial<RetirementAsset> }>) => {
      const { index, updates } = action.payload
      if (state.portfolio[index]) {
        state.portfolio[index] = { ...state.portfolio[index], ...updates }
      }
    },
    setRetirementData: (state, action: PayloadAction<Partial<RetirementState>>) => {
      const data = action.payload
      if (data.currentAge !== undefined) state.currentAge = data.currentAge
      if (data.retirementAge !== undefined) state.retirementAge = data.retirementAge
      if (data.lifeExpectancy !== undefined) state.lifeExpectancy = data.lifeExpectancy
      if (data.currentSavings !== undefined) state.currentSavings = data.currentSavings
      if (data.annualContribution !== undefined) state.annualContribution = data.annualContribution
      if (data.contributionAsPercentOfIncome !== undefined) state.contributionAsPercentOfIncome = data.contributionAsPercentOfIncome
      if (data.contributionMode !== undefined) state.contributionMode = data.contributionMode
      if (data.expectedReturn !== undefined) state.expectedReturn = data.expectedReturn
      if (data.inflationRate !== undefined) state.inflationRate = data.inflationRate
      if (data.desiredIncome !== undefined) state.desiredIncome = data.desiredIncome
      if (data.socialSecurityIncome !== undefined) state.socialSecurityIncome = data.socialSecurityIncome
      if (data.socialSecurityStartAge !== undefined) state.socialSecurityStartAge = data.socialSecurityStartAge
      if (data.requiredSavings !== undefined) state.requiredSavings = data.requiredSavings
      if (data.successRate !== undefined) state.successRate = data.successRate
      if (data.portfolio) state.portfolio = data.portfolio
      if (data.chartData) state.chartData = { ...state.chartData, ...data.chartData }
      retirementSlice.caseReducers.calculateRetirement(state)
    },
    setRetirementGoals: (state, action: PayloadAction<Partial<RetirementState>>) => {
      const goals = action.payload
      if (goals.currentAge !== undefined) state.currentAge = goals.currentAge
      if (goals.retirementAge !== undefined) state.retirementAge = goals.retirementAge
      if (goals.lifeExpectancy !== undefined) state.lifeExpectancy = goals.lifeExpectancy
      if (goals.desiredIncome !== undefined) state.desiredIncome = goals.desiredIncome
      if (goals.socialSecurityIncome !== undefined) state.socialSecurityIncome = goals.socialSecurityIncome
      if (goals.socialSecurityStartAge !== undefined) state.socialSecurityStartAge = goals.socialSecurityStartAge
      retirementSlice.caseReducers.calculateRetirement(state)
    },
    calculateRetirement: (state) => {
      const yearsToRetirement = Math.max(0, state.retirementAge - state.currentAge)
      // Calculate years in retirement for future use
      void (state.lifeExpectancy - state.retirementAge)

      if (yearsToRetirement === 0 || state.expectedReturn === 0) {
        state.requiredSavings = 0
        state.successRate = 0
        retirementSlice.caseReducers.generateChartData(state)
        return
      }

      // Calculate future value of current savings
      const futureValue = state.currentSavings * Math.pow(1 + state.expectedReturn / 100, yearsToRetirement)

      // Calculate future value of annual contributions
      const contributionFutureValue =
        state.annualContribution *
        ((Math.pow(1 + state.expectedReturn / 100, yearsToRetirement) - 1) / (state.expectedReturn / 100))

      // Total portfolio at retirement
      const totalPortfolio = futureValue + contributionFutureValue

      // Calculate required income in retirement (inflation-adjusted)
      const inflationAdjustedIncome = state.desiredIncome * Math.pow(1 + state.inflationRate / 100, yearsToRetirement)

      // Social Security (adjusted for inflation)
      const inflationAdjustedSS = state.socialSecurityIncome * Math.pow(1 + state.inflationRate / 100, yearsToRetirement)

      // Calculate required savings using 3.33% withdrawal rate
      const withdrawalRate = 0.0333
      const requiredFromPortfolio = Math.max(0, inflationAdjustedIncome - inflationAdjustedSS)
      state.requiredSavings = requiredFromPortfolio / withdrawalRate

      // Calculate success rate
      const successRate = state.requiredSavings > 0 ? Math.min(100, (totalPortfolio / state.requiredSavings) * 100) : 100
      state.successRate = Math.max(0, successRate)

      // Generate chart data
      retirementSlice.caseReducers.generateChartData(state)
    },
    generateChartData: (state) => {
      const labels: string[] = []
      const portfolioValues: number[] = []
      const requiredSavings: number[] = []

      for (let year = state.currentAge; year <= state.lifeExpectancy; year++) {
        labels.push(`Age ${year}`)

        if (year <= state.retirementAge) {
          // Pre-retirement: portfolio grows
          const yearsElapsed = year - state.currentAge
          const futureValue = state.currentSavings * Math.pow(1 + state.expectedReturn / 100, yearsElapsed)
          const rate = state.expectedReturn / 100
          const contributionFutureValue = rate > 0
            ? state.annualContribution * ((Math.pow(1 + rate, yearsElapsed) - 1) / rate)
            : state.annualContribution * yearsElapsed
          portfolioValues.push(futureValue + contributionFutureValue)
        } else {
          // Post-retirement: portfolio decreases
          const yearsInRetirement = year - state.retirementAge
          const withdrawalRate = 0.0333
          const annualWithdrawal = state.requiredSavings * withdrawalRate
          const remainingPortfolio = state.requiredSavings - annualWithdrawal * yearsInRetirement
          portfolioValues.push(Math.max(0, remainingPortfolio))
        }

        // Required savings line (constant after retirement)
        if (year >= state.retirementAge) {
          requiredSavings.push(state.requiredSavings)
        } else {
          requiredSavings.push(0)
        }
      }

      state.chartData = {
        labels,
        portfolioValues,
        requiredSavings,
      }
    },
  },
})

export const {
  updateRetirementSettings,
  addPortfolioAsset,
  removePortfolioAsset,
  updatePortfolioAsset,
  setRetirementData,
  setRetirementGoals,
  calculateRetirement,
} = retirementSlice.actions

export default retirementSlice.reducer
